// src/pages/admin/ManageAthletes.tsx
import React, { useState, useEffect, useRef } from "react";
import Navbar from "../../components/Navbar";
import {
  getAllAthletes,
  getAthletesAggregate,
  createAthleteAsAdmin,
  updateAthlete,
  bulkImportAthletes,
  generateSlug,
} from "../../firebase/athletes";
import type { ParsedAthlete } from "../../firebase/athletes";
import { useTranslation } from "../../hooks/useTranslation";
import { C, FONT } from "../../theme";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/config";
import { findPossibleAthleteMatches, findSameNameAthleteKeys } from "../../utils/duplicateAthletes";
import { setAthletePublicProfile, setAthleteStatuses } from "../../firebase/organizationFoundation";
import { useAuth } from "../../context/AuthContext";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return w;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function initials(firstName: string, lastName: string): string {
  return `${(firstName ?? "").charAt(0)}${(lastName ?? "").charAt(0)}`.toUpperCase();
}

const EMPTY_FORM = {
  athleteId: "",
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  slug: "",
  imageUrl: "",
  createdByCoachEmail: "",
  gender: "",
  nationality: "Georgia",
  countryCode: "GE",
  competitorType: "FEDERATION_MEMBER",
  rankingEligible: true,
  status: "ACTIVE",
  duplicateReviewReason: "",
};

const ManageAthletes: React.FC = () => {
  const { t } = useTranslation();
  const { userRole } = useAuth();
  const canManageIdentity = userRole === "admin";
  const [athletes, setAthletes] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [focusedField, setFocusedField] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [slugFilter, setSlugFilter] = useState("");
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [showSameNameOnly, setShowSameNameOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"CURRENT" | "ACTIVE" | "INACTIVE" | "ARCHIVED" | "ALL">("CURRENT");
  const [qualityFilter, setQualityFilter] = useState<"ALL" | "MISSING_DOB" | "MISSING_PHOTO" | "UNASSIGNED" | "FOREIGN">("ALL");
  const [selectedAthletes, setSelectedAthletes] = useState<Set<string>>(new Set());
  const [bulkStatusWorking, setBulkStatusWorking] = useState(false);
  const [publicProfileWorkingId, setPublicProfileWorkingId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const w = useWindowWidth();
  const isMobile = w < 768;

  useEffect(() => {
    loadAthletes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset pagination on search change
  useEffect(() => { setPage(1); }, [search]);

  const bulkPreviewMemo: ParsedAthlete[] = React.useMemo(() => {
    const lines = bulkText.split("\n").map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0) return [];
    return lines.map((line) => {
      const [firstName, ...rest] = line.split(/\s+/);
      const lastName = rest.join(" ");
      const slug = generateSlug(firstName, lastName);
      const athleteId = t("admin.athletes.field.idAutomatic");
      return { firstName, lastName, slug, athleteId };
    });
  }, [bulkText, t]);

  const sameNameDuplicateIds = React.useMemo(
    () => findSameNameAthleteKeys(athletes),
    [athletes]
  );
  const creationMatches = React.useMemo(
    () => editingId || !form.firstName.trim() || !form.lastName.trim()
      ? []
      : findPossibleAthleteMatches(athletes, form),
    [athletes, editingId, form]
  );

  useEffect(() => {
    const q = search.trim().toLowerCase();
    const slugQ = slugFilter.toLowerCase();
    let filteredList = athletes.filter((a) => {
      const fullName = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim().toLowerCase();
      const matchesSearch =
        a.athleteId?.toLowerCase().includes(q) ||
        a.firstName?.toLowerCase().includes(q) ||
        a.lastName?.toLowerCase().includes(q) ||
        fullName.includes(q);                     // ← NEW: match across both names
      const matchesSlug = slugQ === "" || (a.slug ?? "").toLowerCase().includes(slugQ);
      const status = a.status || "ACTIVE";
      const matchesStatus = statusFilter === "ALL"
        || (statusFilter === "CURRENT" && status !== "ARCHIVED")
        || status === statusFilter;
      const matchesQuality = qualityFilter === "ALL"
        || (qualityFilter === "MISSING_DOB" && !a.dateOfBirth)
        || (qualityFilter === "MISSING_PHOTO" && !a.imageUrl)
        || (qualityFilter === "UNASSIGNED" && !a.club && !a.currentClub && !a.currentOrganizationId)
        || (qualityFilter === "FOREIGN" && (a.competitorType === "EXTERNAL_COMPETITOR" || (a.countryCode && a.countryCode !== "GE")));
      return matchesSearch && matchesSlug && matchesStatus && matchesQuality;
    });
    if (showDuplicatesOnly) {
      filteredList = filteredList.filter((a) => /-\d+$/.test(a.slug ?? ""));
    }
    if (showSameNameOnly) {
      filteredList = filteredList.filter((a, index) =>
        sameNameDuplicateIds.has(a.id || a.athleteId || `index:${index}`)
      );
    }

    filteredList.sort((a, b) => {
      if (a.needsReview && !b.needsReview) return -1;
      if (!a.needsReview && b.needsReview) return 1;
      return (a.lastName ?? "").localeCompare(b.lastName ?? "");
    });
    setFiltered(filteredList);
  }, [search, slugFilter, showDuplicatesOnly, showSameNameOnly, sameNameDuplicateIds, athletes, statusFilter, qualityFilter]);

  const loadAthletes = async () => {
    setLoading(true);
    try {
      // Ranking administrators receive the compact identity/eligibility view;
      // coach-account attribution stays in the full admin-only workflow.
      const data = canManageIdentity ? await getAllAthletes() : await getAthletesAggregate();
      setAthletes(data as any[]);
      setFiltered(data as any[]);
    } catch {
      setError(t("admin.athletes.error.load"));
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ── Image upload ─────────────────────────────────────────────────────────
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!editingId || !form.athleteId.trim()) {
      setError(t("admin.athletes.photo.saveFirst"));
      return;
    }
    if (!file.type.startsWith("image/") || file.size > 2 * 1024 * 1024) {
      setError(t("admin.athletes.photo.invalid"));
      return;
    }
    setUploading(true);
    setError("");
    try {
      const storageRef = ref(storage, `athlete-images/${form.athleteId.trim()}-${Date.now()}`);
      await uploadBytes(storageRef, file, { cacheControl: "public, max-age=2592000, immutable" });
      const url = await getDownloadURL(storageRef);
      setForm((prev) => ({ ...prev, imageUrl: url }));
    } catch (err) {
      console.error("Image upload failed:", err);
      setError(t("admin.athletes.photo.error"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── Save athlete ─────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (!editingId && creationMatches.some((match) => match.strength === "strong") && form.duplicateReviewReason.trim().length < 8) {
        setError(t("admin.athletes.duplicate.reasonRequired"));
        return;
      }
      if (editingId) {
        const { duplicateReviewReason: _creationOnlyReason, ...identityUpdate } = form;
        await updateAthlete(editingId, identityUpdate);
      } else {
        await createAthleteAsAdmin(form as any);
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      setPage(1);
      await loadAthletes();
    } catch {
      setError(t("admin.athletes.error.save"));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (athlete: any) => {
    setForm({
      athleteId: athlete.athleteId ?? "",
      firstName: athlete.firstName ?? "",
      lastName: athlete.lastName ?? "",
      dateOfBirth: athlete.dateOfBirth ?? "",
      slug: athlete.slug ?? "",
      imageUrl: athlete.imageUrl ?? "",
      createdByCoachEmail: athlete.createdByCoachEmail ?? "",
      gender: athlete.gender ?? "",
      nationality: athlete.nationality ?? athlete.country ?? "Georgia",
      countryCode: athlete.countryCode ?? "GE",
      competitorType: athlete.competitorType ?? "FEDERATION_MEMBER",
      rankingEligible: athlete.rankingEligible !== false,
      status: athlete.status ?? "ACTIVE",
      duplicateReviewReason: "",
    });
    setEditingId(athlete.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const changeAthleteStatus = async (athleteDocIds: string[], status: "ACTIVE" | "INACTIVE" | "ARCHIVED") => {
    if (!canManageIdentity || athleteDocIds.length === 0) return;
    const reason = window.prompt(t(`admin.athletes.status.reason.${status}`)) || "";
    if (!reason.trim()) return;
    if (!window.confirm(t("admin.athletes.status.confirm").replace("{count}", String(athleteDocIds.length)).replace("{status}", t(`admin.athletes.status.${status}`)))) return;
    setBulkStatusWorking(true); setError("");
    try {
      await setAthleteStatuses(athleteDocIds, status, reason);
      setSelectedAthletes(new Set());
      setPage(1);
      await loadAthletes();
    } catch (err) {
      console.error("Athlete status update failed", err);
      setError(t("admin.athletes.status.error"));
    } finally { setBulkStatusWorking(false); }
  };

  const changePublicProfile = async (athlete: any) => {
    const publish = athlete.publicProfileEnabled !== true;
    if (!window.confirm(t(`admin.athletes.publicProfile.${publish ? "publishConfirm" : "unpublishConfirm"}`))) return;
    const reason = window.prompt(t("admin.athletes.publicProfile.reason"));
    if (reason === null) return;
    setPublicProfileWorkingId(athlete.id);
    setError("");
    try {
      await setAthletePublicProfile(athlete.id, publish, reason);
      await loadAthletes();
    } catch (err) {
      console.error("Public athlete profile change failed", err);
      setError(t("admin.athletes.publicProfile.error"));
    } finally {
      setPublicProfileWorkingId(null);
    }
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
    setError("");
  };

  const handleMarkReviewed = async (docId: string) => {
    await updateAthlete(docId, { needsReview: false });
    await loadAthletes();
  };

  const handleBulkImport = async () => {
    setBulkError("");
    setBulkResult("");
    const lines = bulkText.split("\n").map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0) return;
    if (!window.confirm(t("admin.athletes.bulk.confirm").replace("{count}", String(lines.length)))) return;
    const existingSlugs = new Set(athletes.map(a => a.slug).filter(Boolean) as string[]);
    setBulkImporting(true);
    try {
      const { created } = await bulkImportAthletes(lines, existingSlugs);
      setBulkResult(t("admin.athletes.bulk.success").replace("{count}", String(created)));
      setBulkText("");
      setPage(1);
      await loadAthletes();
    } catch (err: any) {
      console.error("Athlete import failed", err);
      setBulkError(t("admin.athletes.bulk.error"));
    } finally {
      setBulkImporting(false);
    }
  };

  const inputStyle = (field: string): React.CSSProperties => ({
    ...s.input,
    ...(focusedField === field ? s.inputFocused : {}),
  });

  return (
    <div style={s.page} className="page-content">
      <Navbar />
      <div style={{ ...s.container, padding: isMobile ? "20px 16px" : "40px 24px" }}>

        {/* Header */}
        <div style={{ ...s.header, flexDirection: isMobile ? "column" : "row", gap: isMobile ? "12px" : undefined }}>
          <div>
            <div style={s.pageLabel}>{t("admin.athletes.pageLabel")}</div>
            <div style={{ ...s.title, fontSize: isMobile ? "26px" : "36px" }}>{t("admin.athletes.title")}</div>
            <div style={s.subtitle}>{athletes.length} {t("admin.athletes.subtitle")}</div>
          </div>
          {!showForm && canManageIdentity && (
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <button style={s.primaryBtn} onClick={() => setShowForm(true)}>
                {t("admin.athletes.addBtn")}
              </button>
              <button style={s.secondaryBtn} onClick={() => setShowBulkImport(!showBulkImport)}>
                {t("admin.athletes.bulk.open")}
              </button>
            </div>
          )}
        </div>

        {/* ── Bulk Import Panel ─────────────────────────────────────────────── */}
        {showBulkImport && (
          <div style={s.formCard}>
            <div style={s.formCardAccent} />
            <div style={s.formCardBody}>
              <div style={s.formTitle}>{t("admin.athletes.bulk.title")}</div>
              <div style={s.advancedWarning}>{t("admin.athletes.bulk.warning")}</div>

              <textarea
                style={s.bulkTextarea}
                rows={8}
                placeholder={t("admin.athletes.bulk.placeholder")}
                value={bulkText}
                onChange={e => setBulkText(e.target.value)}
              />

              {bulkPreviewMemo.length > 0 && (
                <div style={{ marginTop: "16px" }}>
                  <div style={s.label}>{t("admin.athletes.bulk.preview")}</div>
                  <table style={{ ...s.table, marginTop: "4px" }}>
                    <thead>
                      <tr style={s.thead}>
                        <th style={s.th}>#</th>
                        <th style={s.th}>{t("admin.athletes.field.firstName")}</th>
                        <th style={s.th}>{t("admin.athletes.field.lastName")}</th>
                        <th style={s.th}>Slug</th>
                        <th style={s.th}>{t("admin.athletes.field.id")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPreviewMemo.slice(0, 5).map((a, i) => (
                        <tr key={i} style={s.tr}>
                          <td style={s.td}>{i + 1}</td>
                          <td style={s.td}>{a.firstName}</td>
                          <td style={s.td}>{a.lastName}</td>
                          <td style={s.td}>{a.slug}</td>
                          <td style={s.td}>{a.athleteId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {bulkPreviewMemo.length > 5 && (
                    <div style={{ fontSize: "12px", color: C.text2, marginTop: "4px" }}>
                      {t("admin.athletes.bulk.more").replace("{count}", String(bulkPreviewMemo.length - 5))}
                    </div>
                  )}
                </div>
              )}

              {bulkError && <div style={{ ...s.errorMsg, marginTop: "12px" }}>{bulkError}</div>}
              {bulkResult && <div style={{ ...s.successMsg, marginTop: "12px" }}>{bulkResult}</div>}

              <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
                <button
                  style={s.primaryBtn}
                  onClick={handleBulkImport}
                  disabled={bulkText.trim().length === 0 || bulkImporting}
                >
                  {t("admin.athletes.bulk.import").replace("{count}", String(bulkPreviewMemo.length))}
                </button>
                <button
                  style={s.secondaryBtn}
                  onClick={() => {
                    setShowBulkImport(false);
                    setBulkText("");
                    setBulkError("");
                    setBulkResult("");
                  }}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Form ─────────────────────────────────────────────────────────── */}
        {showForm && (
          <div style={s.formCard}>
            <div style={s.formCardAccent} />
            <div style={s.formCardBody}>
              <div style={s.formTitle}>
                {editingId ? t("admin.athletes.form.edit") : t("admin.athletes.form.add")}
              </div>
              <form onSubmit={handleSubmit} style={s.form}>
                <div style={s.formGrid}>
                  {/* Athlete ID */}
                  <div style={s.field}>
                    <label style={s.label}>{t("admin.athletes.field.id")}</label>
                    <input name="athleteId" value={editingId ? form.athleteId : t("admin.athletes.field.idAutomatic")} readOnly disabled
                      style={inputStyle("athleteId")}
                      onFocus={() => setFocusedField("athleteId")} onBlur={() => setFocusedField("")} />
                  </div>

                  {/* First name */}
                  <div style={s.field}>
                    <label style={s.label}>{t("admin.athletes.field.firstName")}</label>
                    <input name="firstName" value={form.firstName} onChange={handleFormChange}
                      placeholder="First name" required
                      style={inputStyle("firstName")}
                      onFocus={() => setFocusedField("firstName")} onBlur={() => setFocusedField("")} />
                  </div>

                  {/* Last name */}
                  <div style={s.field}>
                    <label style={s.label}>{t("admin.athletes.field.lastName")}</label>
                    <input name="lastName" value={form.lastName} onChange={handleFormChange}
                      placeholder="Last name" required
                      style={inputStyle("lastName")}
                      onFocus={() => setFocusedField("lastName")} onBlur={() => setFocusedField("")} />
                  </div>

                  {/* DOB — optional */}
                  <div style={s.field}>
                    <label style={s.label}>
                      {t("admin.athletes.field.dob")}
                      <span style={{ color: C.text3, fontWeight: 400, marginLeft: 4 }}>{t("admin.athletes.field.optional")}</span>
                    </label>
                    <input name="dateOfBirth" type="date" value={form.dateOfBirth}
                      onChange={handleFormChange}
                      style={inputStyle("dob")}
                      onFocus={() => setFocusedField("dob")} onBlur={() => setFocusedField("")} />
                    {!form.dateOfBirth && (
                      <span style={s.hintText}>{t("admin.athletes.field.missingDobHelp")}</span>
                    )}
                  </div>

                  {/* Slug */}
                  <div style={s.field}>
                    <label style={s.label}>{t("admin.athletes.field.gender")}</label>
                    <select style={s.input} value={form.gender} onChange={(event) => setForm((value) => ({ ...value, gender: event.target.value }))}>
                      <option value="">{t("admin.athletes.field.notRecorded")}</option>
                      <option value="Male">{t("admin.athletes.field.male")}</option>
                      <option value="Female">{t("admin.athletes.field.female")}</option>
                    </select>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>{t("admin.athletes.field.nationality")}</label>
                    <input name="nationality" value={form.nationality} onChange={handleFormChange} style={inputStyle("nationality")} />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>{t("admin.athletes.field.countryCode")}</label>
                    <input name="countryCode" value={form.countryCode} maxLength={2} onChange={(event) => setForm((value) => ({ ...value, countryCode: event.target.value.toUpperCase() }))} style={inputStyle("countryCode")} />
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>{t("admin.athletes.field.competitorType")}</label>
                    <select style={s.input} value={form.competitorType} onChange={(event) => {
                      const competitorType = event.target.value;
                      setForm((value) => ({ ...value, competitorType, rankingEligible: competitorType === "FEDERATION_MEMBER" ? value.rankingEligible : false }));
                    }}>
                      <option value="FEDERATION_MEMBER">{t("admin.athletes.field.federationMember")}</option>
                      <option value="EXTERNAL_COMPETITOR">{t("admin.athletes.field.externalCompetitor")}</option>
                    </select>
                    <label style={{ display: "flex", alignItems: "center", gap: 7, color: C.text2, fontSize: 12 }}><input type="checkbox" checked={form.rankingEligible} onChange={(event) => setForm((value) => ({ ...value, rankingEligible: event.target.checked }))} />{t("admin.athletes.field.rankingEligible")}</label>
                  </div>

                  {/* Slug */}
                  <div style={s.field}>
                    <label style={s.label}>{t("admin.athletes.field.slug")}</label>
                    <input name="slug" value={form.slug} onChange={handleFormChange}
                      placeholder={t("admin.athletes.field.slugPlaceholder")}
                      pattern="[a-z0-9\-]+" title="Lowercase letters, numbers and hyphens only"
                      style={inputStyle("slug")}
                      onFocus={() => setFocusedField("slug")} onBlur={() => setFocusedField("")} />
                    <span style={s.hintText}>{t("admin.athletes.field.slugHelp")}</span>
                  </div>

                  {/* Image upload */}
                  <div style={s.field}>
                    <label style={s.label}>{t("admin.athletes.photo.title")}</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {form.imageUrl ? (
                        <img src={form.imageUrl} alt="Preview"
                          style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: `2px solid ${C.border}` }} />
                      ) : (
                        <div style={s.avatarPlaceholder}>
                          {initials(form.firstName, form.lastName) || "?"}
                        </div>
                      )}
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <button type="button" style={s.uploadBtn}
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading || !editingId}>
                          {uploading ? t("admin.athletes.photo.uploading") : t("admin.athletes.photo.upload")}
                        </button>
                        {form.imageUrl && (
                          <button type="button" style={s.removeImgBtn}
                            onClick={() => setForm((p) => ({ ...p, imageUrl: "" }))}>
                            {t("admin.athletes.photo.remove")}
                          </button>
                        )}
                      </div>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*"
                      style={{ display: "none" }} onChange={handleImageUpload} />
                    <span style={s.hintText}>{editingId ? t("admin.athletes.photo.help") : t("admin.athletes.photo.saveFirst")}</span>
                  </div>
                </div>

                {!editingId && creationMatches.length > 0 && <div style={s.possibleMatches}>
                  <strong>{t("admin.athletes.duplicate.title")}</strong>
                  <div style={s.hintText}>{t("admin.athletes.duplicate.help")}</div>
                  {creationMatches.slice(0, 5).map(({ athlete, strength }) => <div key={athlete.id || athlete.athleteId} style={{ ...s.possibleMatchRow, gridTemplateColumns: isMobile ? "1fr" : "minmax(140px,1fr) minmax(180px,1.5fr) auto" }}>
                    <span>{athlete.firstName} {athlete.lastName}</span>
                    <span>{athlete.athleteId || "—"} · {athlete.dateOfBirth || t("admin.athletes.field.notRecorded")} · {athlete.club || "—"}</span>
                    <span style={{ color: strength === "strong" ? C.red : C.text2 }}>{strength === "strong" ? t("admin.athletes.duplicate.strong") : t("admin.athletes.duplicate.sameName")}</span>
                  </div>)}
                  {creationMatches.some((match) => match.strength === "strong") && <div style={{ marginTop: 10 }}>
                    <label style={s.label}>{t("admin.athletes.duplicate.reason")}</label>
                    <input name="duplicateReviewReason" value={form.duplicateReviewReason} onChange={handleFormChange} style={s.input} placeholder={t("admin.athletes.duplicate.reasonPlaceholder")} />
                  </div>}
                </div>}

                {error && <div style={s.errorMsg}>{error}</div>}

                <div style={s.formActions}>
                  <button type="submit" style={s.primaryBtn} disabled={saving || uploading}>
                    {saving ? t("admin.athletes.form.saving") : editingId ? t("admin.athletes.save") : t("admin.athletes.create")}
                  </button>
                  <button type="button" style={s.secondaryBtn} onClick={handleCancel}>
                    {t("admin.athletes.cancel")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "16px", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: "200px" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("admin.athletes.search")}
              style={{ ...s.input, ...(focusedField === "search" ? s.inputFocused : {}), width: "100%" }}
              onFocus={() => setFocusedField("search")} onBlur={() => setFocusedField("")} />
          </div>
          <div style={{ width: "180px" }}>
            <input value={slugFilter} onChange={(e) => setSlugFilter(e.target.value)}
              placeholder={t("admin.athletes.field.slugFilter")}
              style={{ ...s.input, ...(focusedField === "slugFilter" ? s.inputFocused : {}), width: "100%", fontSize: "12px" }}
              onFocus={() => setFocusedField("slugFilter")} onBlur={() => setFocusedField("")} />
          </div>
          <button
            style={{
              ...s.secondaryBtn,
              backgroundColor: showDuplicatesOnly ? C.red : C.surface2,
              color: showDuplicatesOnly ? "#fff" : C.text2,
              borderColor: showDuplicatesOnly ? C.red : C.border2,
              fontWeight: 700,
              fontSize: "12px",
              padding: "8px 14px",
              whiteSpace: "nowrap",
            }}
            onClick={() => {
              setShowDuplicatesOnly(!showDuplicatesOnly);
              setShowSameNameOnly(false);
              setPage(1);
            }}
          >
            {showDuplicatesOnly ? t("admin.athletes.duplicates.showAll") : t("admin.athletes.duplicates.filter")}
          </button>
          <button
            style={{
              ...s.secondaryBtn,
              backgroundColor: showSameNameOnly ? C.red : C.surface2,
              color: showSameNameOnly ? "#fff" : C.text2,
              borderColor: showSameNameOnly ? C.red : C.border2,
              fontWeight: 700,
              fontSize: "12px",
              padding: "8px 14px",
              whiteSpace: "nowrap",
            }}
            onClick={() => {
              setShowSameNameOnly(!showSameNameOnly);
              setShowDuplicatesOnly(false);
              setPage(1);
            }}
          >
            {showSameNameOnly
              ? t("admin.athletes.sameName.showAll")
              : `${t("admin.athletes.sameName.filter")} (${sameNameDuplicateIds.size})`}
          </button>
          <select style={{ ...s.input, minWidth: 145 }} value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as typeof statusFilter); setPage(1); setSelectedAthletes(new Set()); }}>
            <option value="CURRENT">{t("admin.athletes.status.current")}</option>
            <option value="ACTIVE">{t("admin.athletes.status.ACTIVE")}</option>
            <option value="INACTIVE">{t("admin.athletes.status.INACTIVE")}</option>
            <option value="ARCHIVED">{t("admin.athletes.status.ARCHIVED")}</option>
            <option value="ALL">{t("admin.athletes.status.all")}</option>
          </select>
          <select style={{ ...s.input, minWidth: 160 }} value={qualityFilter} onChange={(event) => { setQualityFilter(event.target.value as typeof qualityFilter); setPage(1); setSelectedAthletes(new Set()); }}>
            <option value="ALL">{t("admin.athletes.quality.all")}</option>
            <option value="MISSING_DOB">{t("admin.athletes.quality.missingDob")}</option>
            <option value="MISSING_PHOTO">{t("admin.athletes.quality.missingPhoto")}</option>
            <option value="UNASSIGNED">{t("admin.athletes.quality.unassigned")}</option>
            <option value="FOREIGN">{t("admin.athletes.quality.foreign")}</option>
          </select>
          {search && <span style={s.searchCount}>{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>}
        </div>

        {showSameNameOnly && (
          <div style={{ ...s.errorMsg, background: C.redDim, color: C.text, marginBottom: "14px" }}>
            {t("admin.athletes.sameName.help")}
          </div>
        )}

        {canManageIdentity && selectedAthletes.size > 0 && <div style={s.bulkActionBar}>
          <strong>{t("admin.athletes.status.selected").replace("{count}", String(selectedAthletes.size))}</strong>
          <button style={s.secondaryBtn} disabled={bulkStatusWorking} onClick={() => changeAthleteStatus([...selectedAthletes], "ACTIVE")}>{t("admin.athletes.status.restore")}</button>
          <button style={s.secondaryBtn} disabled={bulkStatusWorking} onClick={() => changeAthleteStatus([...selectedAthletes], "INACTIVE")}>{t("admin.athletes.status.makeInactive")}</button>
          <button style={s.deleteBtn} disabled={bulkStatusWorking} onClick={() => changeAthleteStatus([...selectedAthletes], "ARCHIVED")}>{t("admin.athletes.status.archive")}</button>
        </div>}

        {/* Table */}
        {loading ? (
          <div style={s.stateMsg}>{t("admin.athletes.loading")}</div>
        ) : filtered.length === 0 ? (
          <div style={s.stateMsg}>{search ? t("admin.athletes.noMatch") : t("admin.athletes.empty")}</div>
        ) : (
          <div style={{ ...s.tableWrapper, WebkitOverflowScrolling: "touch" as any }}>
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  {canManageIdentity && <th style={s.th}><input type="checkbox" aria-label={t("admin.athletes.status.selectPage")} checked={filtered.slice((page - 1) * pageSize, page * pageSize).length > 0 && filtered.slice((page - 1) * pageSize, page * pageSize).every((a) => selectedAthletes.has(a.id))} onChange={() => {
                    const rows = filtered.slice((page - 1) * pageSize, page * pageSize); const all = rows.every((a) => selectedAthletes.has(a.id)); setSelectedAthletes((previous) => { const next = new Set(previous); rows.forEach((a) => all ? next.delete(a.id) : next.add(a.id)); return next; });
                  }} /></th>}
                  <th style={s.th}>Photo</th>
                  <th style={s.th}>{t("admin.athletes.field.id")}</th>
                  <th style={s.th}>{t("admin.athletes.field.firstName")}</th>
                  <th style={s.th}>{t("admin.athletes.field.lastName")}</th>
                  <th style={s.th}>{t("admin.athletes.field.dob")}</th>
                  <th style={s.th}>{t("admin.athletes.field.club")}</th>
                  <th style={s.th}>{t("admin.athletes.field.nationality")}</th>
                  <th style={s.th}>Slug</th>
                  {canManageIdentity && <th style={s.th}>{t("admin.athletes.field.addedBy")}</th>}
                  <th style={s.th}>Reviewed</th>
                  <th style={s.th}>{t("admin.athletes.publicProfile.label")}</th>
                  <th style={s.th}>{t("admin.athletes.status.label")}</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice((page - 1) * pageSize, page * pageSize).map((a) => (
                  <tr key={a.id} style={s.tr}>
                    {canManageIdentity && <td style={s.td}><input type="checkbox" checked={selectedAthletes.has(a.id)} onChange={() => setSelectedAthletes((previous) => { const next = new Set(previous); next.has(a.id) ? next.delete(a.id) : next.add(a.id); return next; })} /></td>}
                    <td style={s.td}>
                      {a.imageUrl ? (
                        <img src={a.imageUrl} alt=""
                          style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: `1px solid ${C.border}`, display: "block" }} />
                      ) : (
                        <div style={{ ...s.avatarPlaceholder, width: 32, height: 32, fontSize: "11px" }}>
                          {initials(a.firstName, a.lastName)}
                        </div>
                      )}
                    </td>
                    <td style={{ ...s.td, fontFamily: "monospace", color: C.red, fontSize: "12px" }}>{a.athleteId}</td>
                    <td style={s.td}>{a.firstName}</td>
                    <td style={{ ...s.td, fontWeight: 600, color: C.text }}>{a.lastName}</td>
                    <td style={{ ...s.td, color: a.dateOfBirth ? C.text2 : C.text3, fontSize: "13px" }}>
                      {a.dateOfBirth || <span title="No date of birth on record">—</span>}
                    </td>
                    <td style={{ ...s.td, color: C.text2, fontSize: "12px" }}>
                      {a.club || a.currentClub || "—"}
                    </td>
                    <td style={{ ...s.td, color: C.text2, fontSize: "12px" }}>
                      {a.nationality || a.country || a.flag || "—"}
                    </td>
                    <td style={{ ...s.td, fontFamily: "monospace", fontSize: "12px", color: C.text2 }}>
                      {a.slug ?? <span style={{ color: C.text3 }}>—</span>}
                    </td>
                    {canManageIdentity && <td style={{ ...s.td, fontSize: "12px", color: C.text2 }}>
                      {a.createdByCoachEmail
                        ? a.createdByCoachEmail
                        : a.needsReview === true
                          ? <span style={{ color: C.text3 }}>Coach (unknown)</span>
                          : <span style={{ color: C.text3 }}>Admin</span>
                      }
                    </td>}
                    <td style={{ ...s.td, textAlign: "center" }}>
                      {a.needsReview === true ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                          <span style={{
                            width: "8px",
                            height: "8px",
                            backgroundColor: C.red,
                            borderRadius: "50%",
                            display: "inline-block",
                          }} title="Added by coach — needs review" />
                          {canManageIdentity ? <button
                            style={s.reviewBtn}
                            onClick={() => handleMarkReviewed(a.id)}
                          >
                            ✓ Review
                          </button> : <small>{t("admin.athletes.status.pendingReview")}</small>}
                        </span>
                      ) : (
                        <span style={{ color: C.text3, fontSize: "12px" }}>—</span>
                      )}
                    </td>
                    <td style={s.td}>
                      <div style={{ display: "grid", gap: 6, justifyItems: "start" }}>
                        <span style={{ ...s.statusBadge, ...(a.publicProfileEnabled === true ? s.statusActive : s.statusInactive) }}>
                          {t(`admin.athletes.publicProfile.${a.publicProfileEnabled === true ? "published" : "notPublished"}`)}
                        </span>
                        {canManageIdentity && <button
                          type="button"
                          style={a.publicProfileEnabled === true ? s.secondaryBtn : s.editBtn}
                          disabled={publicProfileWorkingId === a.id || (a.publicProfileEnabled !== true && (a.status || "ACTIVE") !== "ACTIVE")}
                          onClick={() => changePublicProfile(a)}
                        >
                          {t(`admin.athletes.publicProfile.${a.publicProfileEnabled === true ? "unpublish" : "publish"}`)}
                        </button>}
                      </div>
                    </td>
                    <td style={s.td}><span style={{ ...s.statusBadge, ...(a.status === "ARCHIVED" ? s.statusArchived : a.status === "INACTIVE" ? s.statusInactive : s.statusActive) }}>{t(`admin.athletes.status.${a.status || "ACTIVE"}`)}</span></td>
                    <td style={s.td}>
                      {canManageIdentity ? <>
                        <button style={s.editBtn} onClick={() => handleEdit(a)}>{t("admin.athletes.edit")}</button>
                        {a.status === "ARCHIVED"
                          ? <button style={s.editBtn} onClick={() => changeAthleteStatus([a.id], "ACTIVE")}>{t("admin.athletes.status.restore")}</button>
                          : <button style={s.deleteBtn} onClick={() => changeAthleteStatus([a.id], "ARCHIVED")}>{t("admin.athletes.status.archive")}</button>}
                      </> : <span style={{ color: C.text3 }}>{t("admin.athletes.status.viewOnly")}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {filtered.length > pageSize && (
          <div style={s.paginationRow}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "13px", color: C.text2 }}>Rows per page:</span>
              <select
                style={s.selectSmall}
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              >
                <option value={25}>25</option>
                <option value={40}>40</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div style={{ fontSize: "13px", color: C.text2 }}>
              Page {page} of {Math.ceil(filtered.length / pageSize)} ({filtered.length} athletes)
            </div>

            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              <button
                style={s.paginationBtn}
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                ← Prev
              </button>
              {(() => {
                const total = Math.ceil(filtered.length / pageSize);
                const pages: number[] = [];
                if (total <= 7) {
                  for (let i = 1; i <= total; i++) pages.push(i);
                } else {
                  pages.push(1, 2);
                  if (page > 3) pages.push(-1);
                  if (page > 2 && page < total - 1) pages.push(page);
                  if (page < total - 2) pages.push(-1);
                  pages.push(total - 1, total);
                }
                return pages.map((p, i) =>
                  p === -1 ? (
                    <span key={`ellipsis-${i}`} style={{ color: C.text3, padding: "0 4px" }}>…</span>
                  ) : (
                    <button
                      key={p}
                      style={p === page ? s.paginationBtnActive : s.paginationBtn}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  )
                );
              })()}
              <button
                style={s.paginationBtn}
                disabled={page === Math.ceil(filtered.length / pageSize)}
                onClick={() => setPage(p => p + 1)}
              >
                Next →
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: { maxWidth: "1100px", margin: "0 auto", padding: "40px 24px" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px" },
  pageLabel: { fontSize: "10px", fontWeight: 700, letterSpacing: "2px", color: C.red, marginBottom: "4px" },
  title: { fontFamily: FONT.display, fontSize: "36px", letterSpacing: "1px", color: C.text, marginBottom: "4px", lineHeight: 1 },
  subtitle: { color: C.text2, fontSize: "14px" },
  formCard: { backgroundColor: C.surface, borderRadius: "12px", border: `1px solid ${C.border}`, marginBottom: "24px", overflow: "hidden" },
  formCardAccent: { height: "3px", background: `linear-gradient(90deg, ${C.red}, rgba(218,41,28,0.2))` },
  formCardBody: { padding: "24px" },
  formTitle: { fontSize: "16px", fontWeight: 700, color: C.text, marginBottom: "20px" },
  form: { display: "flex", flexDirection: "column", gap: "16px" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "16px" },
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  label: { fontSize: "10px", fontWeight: 700, color: C.text2, textTransform: "uppercase", letterSpacing: "0.8px" },
  hintText: { fontSize: "11px", color: C.text3, lineHeight: 1.4 },
  input: { padding: "10px 12px", border: `1px solid ${C.border2}`, borderRadius: "7px", fontSize: "14px", outline: "none", color: C.text, backgroundColor: C.surface2, transition: "border-color 0.15s, box-shadow 0.15s" },
  inputFocused: { border: `1px solid ${C.red}`, boxShadow: `0 0 0 3px ${C.redDim}` },
  avatarPlaceholder: { width: 44, height: 44, borderRadius: "50%", backgroundColor: C.surface2, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: 700, color: C.text2, flexShrink: 0 },
  uploadBtn: { padding: "6px 14px", fontSize: "12px", border: `1px solid ${C.border2}`, borderRadius: "6px", backgroundColor: C.surface, cursor: "pointer", fontWeight: 600, color: C.text2 },
  removeImgBtn: { padding: "4px 10px", fontSize: "11px", border: "none", borderRadius: "5px", backgroundColor: C.redDim, color: C.red, cursor: "pointer", fontWeight: 600 },
  formActions: { display: "flex", gap: "12px" },
  errorMsg: { backgroundColor: C.redDim, border: `1px solid rgba(218,41,28,0.3)`, borderRadius: "7px", padding: "10px 14px", fontSize: "13px", color: C.red },
  searchRow: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" },
  searchCount: { fontSize: "12px", color: C.text2 },
  stateMsg: { color: C.text2, fontSize: "14px", padding: "40px 0", textAlign: "center" },
  bulkActionBar: { position: "sticky", top: 102, zIndex: 5, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", background: C.surface, boxShadow: "0 8px 22px rgba(15,23,42,.08)", color: C.text },
  statusBadge: { display: "inline-block", borderRadius: 999, padding: "4px 8px", fontSize: 10, fontWeight: 800 },
  statusActive: { background: "rgba(22,163,74,.12)", color: C.success },
  statusInactive: { background: C.surface2, color: C.text2 },
  statusArchived: { background: C.redDim, color: C.red },
  tableWrapper: { overflowX: "auto", borderRadius: "12px", border: `1px solid ${C.border}` },
  table: { width: "100%", borderCollapse: "collapse", backgroundColor: C.surface, minWidth: "640px" },
  thead: { backgroundColor: C.surface2 },
  th: { padding: "12px 16px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: C.text2, textTransform: "uppercase", letterSpacing: "0.8px", borderBottom: `1px solid ${C.border}` },
  tr: { borderBottom: `1px solid ${C.border}` },
  td: { padding: "10px 16px", fontSize: "14px", color: C.text2 },
  primaryBtn: { backgroundColor: C.red, color: "#fff", border: "none", borderRadius: "7px", padding: "10px 20px", fontSize: "14px", fontWeight: 700, cursor: "pointer" },
  secondaryBtn: { backgroundColor: C.surface2, color: C.text2, border: `1px solid ${C.border2}`, borderRadius: "7px", padding: "10px 20px", fontSize: "14px", cursor: "pointer" },
  editBtn: { backgroundColor: C.surface2, border: `1px solid ${C.border}`, color: C.text2, borderRadius: "6px", padding: "5px 12px", fontSize: "12px", cursor: "pointer", marginRight: "8px", fontWeight: 500 },
  deleteBtn: { backgroundColor: C.redDim, color: C.red, border: `1px solid rgba(218,41,28,0.2)`, borderRadius: "6px", padding: "5px 12px", fontSize: "12px", cursor: "pointer", fontWeight: 500 },
  reviewBtn: { backgroundColor: C.surface2, border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "11px", color: C.text2, cursor: "pointer", padding: "2px 6px", whiteSpace: "nowrap", fontWeight: 500 },
  // Bulk import styles
  bulkTextarea: { width: "100%", padding: "10px 12px", border: `1px solid ${C.border2}`, borderRadius: "7px", fontSize: "13px", fontFamily: "monospace", outline: "none", color: C.text, backgroundColor: C.surface2, resize: "vertical" as const },
  advancedWarning: { margin: "10px 0 14px", padding: "10px 12px", border: `1px solid ${C.warningBorder}`, borderRadius: 8, background: C.warningDim, color: C.warning, fontSize: 12, lineHeight: 1.5 },
  possibleMatches: { marginTop: 14, padding: 14, border: `1px solid ${C.warningBorder}`, borderRadius: 9, background: C.warningDim, color: C.text, fontSize: 13 },
  possibleMatchRow: { display: "grid", gap: 10, alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}` },
  successMsg: { backgroundColor: C.successDim, border: `1px solid ${C.successBorder}`, borderRadius: "7px", padding: "10px 14px", fontSize: "13px", color: C.success, fontWeight: 600 },
  // Pagination styles
  paginationRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px", flexWrap: "wrap" as const, gap: "12px" },
  selectSmall: { padding: "6px 8px", fontSize: "13px", border: `1px solid ${C.border2}`, borderRadius: "6px", backgroundColor: C.surface, color: C.text },
  paginationBtn: { backgroundColor: C.surface2, color: C.text2, border: `1px solid ${C.border2}`, borderRadius: "6px", padding: "4px 10px", fontSize: "12px", cursor: "pointer", fontWeight: 500 },
  paginationBtnActive: { backgroundColor: C.red, color: "#fff", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "12px", cursor: "default", fontWeight: 700 },
  // Propagation styles
  propagateBanner: {
    backgroundColor: C.warningDim,
    border: `1px solid ${C.warning}`,
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  propagateBtn: {
    backgroundColor: C.warning,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    alignSelf: "flex-start",
  },
  propagateSuccess: {
    backgroundColor: "var(--color-success-soft)",
    border: "1px solid #6ee7b7",
    borderRadius: "7px",
    padding: "12px 16px",
    fontSize: "13px",
    color: "var(--color-success)",
    fontWeight: 600,
    marginBottom: "16px",
    lineHeight: 1.6,
  },
};

export default ManageAthletes;
